import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow'
import { Download, Check, Loader2 } from 'lucide-react'
import Modal from '../ui/Modal'
import { cn } from '../../lib/utils'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../contexts/ActiveCanvasIdContext'
import { canvasRoomKeyOf, listRemovedAssets } from './removedAssetMemory'
import { api, buildProjectAssetUrl, getAuthToken, getPublicationToken } from '../../api/client'
import { getThumbnailUrl } from '../../api/useAssetUrl'
import { buildDownloadCandidates, type CanvasAssetCandidate, type MediaType } from './nodes/shared/mentionUtils'
import { IMAGE_ICON, VIDEO_ICON } from './nodes/shared/ReferenceSelector'
import { AudioWaveformVisualization } from './nodes/shared/AudioWaveformVisualization'
import { toast } from 'sonner'
import { createDebug } from '../../utils/debugLog'
import {
  buildAssetDownloadFilename,
  buildClipDownloadFilename,
} from '../../utils/buildDownloadFilename'
import {
  beginAssetDownload,
  currentWorkspaceViewMode,
  newBulkActionId,
  type AssetDownloadAttempt,
} from '../../analytics/assetDownloadAnalytics'
import { outputVariantId } from '../../analytics/outputVariantId'
import type { AssetDownloadEntryPoint } from '../../analytics/generated/analyticsEvents'
import { usePaletteAnalytics, type PaletteInteraction } from '../../analytics/paletteAnalytics'
import { useBooleanFlagValue } from '../../hooks/useBooleanFlagValue'
import { runC2paDownloadOnce } from './nodes/shared/c2paDownloadProgress'
import {
  C2PA_DOWNLOAD_SIGNING_FLAG,
  isC2paSignableDownload,
  newDownloadTraceId,
  resolveC2paDownloadPath,
} from './nodes/shared/useNodeActions'

const log = createDebug('dw:clip-download')

/**
 * RFC-1612: how many selected videos are C2PA-signed at once. Each signing is
 * a ~15-30s APNB round trip, so a strictly sequential bulk download of N clips
 * would take minutes; unbounded fan-out would hammer the shared endpoint.
 */
export const BULK_C2PA_SIGNING_CONCURRENCY = 3

/** Run `worker` over `items` with at most `limit` in flight, preserving nothing but completion. */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let next = 0
  const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next
      next += 1
      await worker(items[index], index)
    }
  })
  await Promise.all(lanes)
}

/**
 * RFC-1577 entry point for this modal: the palette in list view (or list
 * view's rail) is `list_view`; the canvas bottom palette — and any opener with
 * no toolbar interaction — is `canvas_bottom_bar`.
 */
export function clipDownloadEntryPoint(interaction: PaletteInteraction | null): AssetDownloadEntryPoint {
  return interaction?.surface === 'list_palette' || interaction?.surface === 'list_rail'
    ? 'list_view'
    : 'canvas_bottom_bar'
}

const MEDIA_TABS: { key: MediaType; labelKey: string }[] = [
  { key: 'image', labelKey: 'clipDownloadModal.tabImages' },
  { key: 'video', labelKey: 'clipDownloadModal.tabVideos' },
  { key: 'audio', labelKey: 'clipDownloadModal.tabAudio' },
]

interface ClipDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  canvasNodes: ReactFlowNode[]
  canvasEdges: ReactFlowEdge[]
}

export function ClipDownloadModal({ isOpen, onClose, canvasNodes, canvasEdges }: ClipDownloadModalProps) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const activeCanvasId = useActiveCanvasId()
  const c2paSigningEnabled = useBooleanFlagValue(C2PA_DOWNLOAD_SIGNING_FLAG, false)
  const [activeTab, setActiveTab] = useState<MediaType>('image')
  // RFC-1577: attributed to the palette click that opened this modal.
  const analytics = usePaletteAnalytics()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const allCandidates = useMemo(() => {
    if (!isOpen) return []
    const live = buildDownloadCandidates(canvasNodes)
    // Undo removes a node, but not the file it generated — that is still in
    // GCS with nothing pointing at it. Append the assets of nodes undo took
    // away so they stay downloadable. Live entries win on a path collision,
    // which is what makes redo self-healing: the restored node re-supplies its
    // own candidate and no duplicate row appears.
    const livePaths = new Set(live.map((c) => c.assetPath))
    const removed = listRemovedAssets(canvasRoomKeyOf(projectId, activeCanvasId))
      .filter((c) => !livePaths.has(c.assetPath))
    const candidates = [...live, ...removed]
    log('candidates found: %d (image=%d, video=%d, audio=%d, from-removed-nodes=%d)',
      candidates.length,
      candidates.filter(c => c.mediaType === 'image').length,
      candidates.filter(c => c.mediaType === 'video').length,
      candidates.filter(c => c.mediaType === 'audio').length,
      removed.length,
    )
    return candidates
  }, [canvasNodes, isOpen, projectId, activeCanvasId])

  const tabCandidates = useMemo(
    () => allCandidates.filter(c => c.mediaType === activeTab),
    [allCandidates, activeTab],
  )

  const tabCounts = useMemo(() => {
    const counts: Record<MediaType, number> = { image: 0, video: 0, audio: 0 }
    for (const c of allCandidates) counts[c.mediaType]++
    return counts
  }, [allCandidates])

  const candidateKey = (c: CanvasAssetCandidate) => `${c.nodeId}:${c.assetPath}`

  const toggleSelect = useCallback((c: CanvasAssetCandidate) => {
    // Decided outside the updater: an updater must stay pure (StrictMode may
    // run it twice), and the analytics step must fire exactly once.
    const key = candidateKey(c)
    const wasSelected = selected.has(key)
    analytics?.step('download', wasSelected ? 'deselect' : 'select', { option: c.mediaType })
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [analytics, selected])

  const toggleSelectAll = useCallback(() => {
    const keys = tabCandidates.map(candidateKey)
    const allSelected = keys.length > 0 && keys.every(k => selected.has(k))
    analytics?.step('download', allSelected ? 'deselect_all' : 'select_all', {
      option: activeTab,
      item_count: keys.length,
    })
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.delete(k))
        return next
      })
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.add(k))
        return next
      })
    }
  }, [tabCandidates, selected, analytics, activeTab])

  const selectedCount = useMemo(
    () => tabCandidates.filter(c => selected.has(candidateKey(c))).length,
    [tabCandidates, selected],
  )

  const allTabSelected = tabCandidates.length > 0 && selectedCount === tabCandidates.length

  const handleDownload = useCallback(async () => {
    if (!projectId) return

    const toDownload = allCandidates.filter(c => selected.has(candidateKey(c)))
    if (toDownload.length === 0) {
      toast.error(t('clipDownloadModal.noAssetsSelected'))
      return
    }
    analytics?.step('download', 'download', { item_count: toDownload.length })
    // RFC-1577 asset_download_action: one attempt per selected asset, all
    // sharing this click's bulk_action_id. Captured before any await so a
    // close mid-download cannot change the attribution.
    const entryPoint = clipDownloadEntryPoint(analytics?.active('download') ?? null)
    const viewMode = currentWorkspaceViewMode()
    const bulk = { id: newBulkActionId(), count: toDownload.length }

    log('download start: %d assets', toDownload.length)
    setDownloading(true)
    setProgress({ done: 0, total: toDownload.length })

    const token = await getAuthToken()
    const pubToken = getPublicationToken()

    // Download each file individually via hidden iframes — same pattern as
    // useNodeActions.handleDownload. Bypasses CORS entirely (no fetch, no
    // redirect issues). Each file triggers a separate browser save.
    const handOff = (assetPath: string, filename: string, attempt: AssetDownloadAttempt) => {
      let stage: 'url_build_failed' | 'handoff_failed' = 'url_build_failed'
      try {
        const url = new URL(
          buildProjectAssetUrl(projectId, assetPath),
          window.location.origin,
        )
        if (token) url.searchParams.set('token', token)
        if (pubToken) url.searchParams.set('publication_token', pubToken)
        url.searchParams.set('download', filename)

        stage = 'handoff_failed'
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url.toString()
        document.body.appendChild(iframe)
        window.setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
        }, 60_000)
      } catch (err) {
        // Telemetry only — rethrown so the modal behaves exactly as before.
        attempt.failed(stage)
        throw err
      }
    }

    const prepared = toDownload.map((c) => {
      const filename =
        c.mediaType === 'video'
          ? buildClipDownloadFilename({
              canvasNodes,
              canvasEdges,
              nodeId: c.nodeId,
              assetPath: c.assetPath,
              variantHistory: c.variantHistory ?? [],
              pickedIndex: c.pickedIndex ?? 0,
              trigger: 'bulk-download',
            }).filename
          : buildAssetDownloadFilename({
              canvasNodes,
              canvasEdges,
              nodeId: c.nodeId,
              assetPath: c.assetPath,
              mediaType: c.mediaType,
              variantHistory: c.variantHistory,
              pickedIndex: c.pickedIndex,
              trigger: 'bulk-download',
            }).filename
      // RFC-1577 asset_download_action (supersedes the video-only
      // canvas_video_download_initiated): every media type, one per asset.
      const attempt: AssetDownloadAttempt = beginAssetDownload({
        projectId,
        entryPoint,
        target: 'node_output',
        viewMode,
        mediaType: c.mediaType,
        nodeId: c.nodeId,
        variantId: outputVariantId(c.nodeId, c.pickedIndex),
        filename,
        bulk,
      })
      return { candidate: c, filename, attempt }
    })
    const needsSigning = (c: CanvasAssetCandidate, filename: string) =>
      c2paSigningEnabled && c.mediaType === 'video' && isC2paSignableDownload(c.assetPath, filename)
    const immediate = prepared.filter((p) => !needsSigning(p.candidate, p.filename))
    const signed = prepared.filter((p) => needsSigning(p.candidate, p.filename))

    let done = 0
    const markDone = (filename: string) => {
      done += 1
      log('queued %s', filename)
      setProgress({ done, total: toDownload.length })
    }

    for (let i = 0; i < immediate.length; i++) {
      const { candidate, filename, attempt } = immediate[i]
      handOff(candidate.assetPath, filename, attempt)
      markDone(filename)
      if (i < immediate.length - 1 || signed.length > 0) {
        await new Promise(r => setTimeout(r, 300))
      }
    }

    // RFC-1612: sign videos before handing them off. Each owning node shows its
    // own loading state while it signs; failures silently fall back to the
    // original (the backend logs a warning). A video already signing from its
    // node is skipped here (null) — that download is already on its way.
    await runWithConcurrency(signed, BULK_C2PA_SIGNING_CONCURRENCY, async ({ candidate, filename, attempt }) => {
      const resolved = await runC2paDownloadOnce(candidate.assetPath, candidate.nodeId, () =>
        resolveC2paDownloadPath({
          projectId,
          assetPath: candidate.assetPath,
          traceId: newDownloadTraceId(),
          source: 'bulk-download',
          filename,
          nodeId: candidate.nodeId,
          prepare: (pid, path) => api.prepareC2paDownload(pid, path),
          poll: (pid, path) => api.getC2paDownloadStatus(pid, path),
        }),
      )
      if (resolved !== null) handOff(resolved, filename, attempt)
      markDone(filename)
    })

    setDownloading(false)
    toast.success(t('clipDownloadModal.downloadingAssets', { count: toDownload.length }))
  }, [projectId, allCandidates, selected, canvasNodes, canvasEdges, t, analytics, c2paSigningEnabled])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('clipDownloadModal.title')} size="lg">
      <div className="space-y-4">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-border">
          {MEDIA_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.key !== activeTab) analytics?.step('download', 'tab', { option: tab.key })
                setActiveTab(tab.key)
              }}
              className={cn(
                'px-3 py-2 text-xs font-medium transition-colors cursor-pointer',
                activeTab === tab.key
                  ? 'text-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`clip-tab-${tab.key}`}
            >
              {t(tab.labelKey)} ({tabCounts[tab.key]})
            </button>
          ))}
        </div>

        {/* Asset grid */}
        <div className="max-h-[50vh] overflow-y-auto">
          {tabCandidates.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm" data-testid="clip-empty">
              {t('clipDownloadModal.noAssetsInProject', { mediaType: activeTab })}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {tabCandidates.map(c => {
                const key = candidateKey(c)
                const isSelected = selected.has(key)
                const thumbUrl = c.mediaType === 'image' && projectId
                  ? getThumbnailUrl(projectId, c.assetPath, 200)
                  : null
                const assetUrl = (c.mediaType === 'video' || c.mediaType === 'audio') && projectId
                  ? getThumbnailUrl(projectId, c.assetPath, 0)
                  : null

                return (
                  <button
                    key={key}
                    onClick={() => toggleSelect(c)}
                    className={cn(
                      'relative rounded-lg border-2 overflow-hidden cursor-pointer transition-all group',
                      isSelected ? 'border-primary' : 'border-border hover:border-muted-foreground/40',
                    )}
                    data-testid="clip-card"
                  >
                    {/* Thumbnail / preview */}
                    {assetUrl && c.mediaType === 'audio' ? (
                      <div className="bg-muted px-3 py-3 pointer-events-none">
                        <AudioWaveformVisualization url={assetUrl} height={48} barCount={80} compact />
                      </div>
                    ) : (
                      <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt={c.label}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : assetUrl && c.mediaType === 'video' ? (
                          <video
                            src={assetUrl}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                          />
                        ) : (
                          <span className="opacity-30 text-muted-foreground scale-150">
                            {c.mediaType === 'video' ? VIDEO_ICON : IMAGE_ICON}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Checkbox */}
                    <div className={cn(
                      'absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center',
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-black/40 text-white/60',
                    )}>
                      {isSelected && <Check className="w-3.5 h-3.5" />}
                    </div>

                    {/* Label */}
                    <div className="px-2 py-1.5 bg-card">
                      <p className="text-xs font-medium text-muted-foreground truncate">{c.label}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {tabCandidates.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-muted-foreground">
              <input
                type="checkbox"
                checked={allTabSelected}
                onChange={toggleSelectAll}
                className="rounded border-border"
                data-testid="clip-select-all"
              />
              {t('clipDownloadModal.selectAllMediaType', { mediaType: activeTab })}
            </label>

            <div className="flex items-center gap-3">
              {downloading && (
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {progress.done}/{progress.total}
                </div>
              )}
              <button
                onClick={handleDownload}
                disabled={selected.size === 0 || downloading}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors',
                  selected.size > 0 && !downloading
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                    : 'bg-muted text-muted-foreground cursor-not-allowed',
                )}
                data-testid="clip-download-btn"
              >
                <Download className="w-3.5 h-3.5" />
                {downloading
                  ? t('clipDownloadModal.downloading')
                  : selected.size > 0
                    ? t('clipDownloadModal.downloadCount', { count: selected.size })
                    : t('clipDownloadModal.selectAssets')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
